/**
 * The portfolio: every account you have, live — what it is worth, how today went, what you hold, the
 * orders that are working and what filled. Flat on the page, sections parted by hairlines; the order
 * ticket is the one panel. A stock opens as candles with your own lines drawn across them.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ArrowLeft, ChartLine, ChartPie, ChevronRight, Copy as CopyIcon, MessagesSquare, Plus, X } from 'lucide-react'
import type { CopyView } from '@shared/copy'
import { scopePortfolio, type PfAccount, type PfOrder, type PortfolioLive } from '@shared/portfolio'
import type { CompanyProfile, OhlcBar, PortfolioSnapshot, TechnicalAnalysis } from '@shared/types'
import { usePortfolioLive } from '../hooks/usePortfolioLive'
import { useMedia } from './Agents'
import { api, brokerApi } from './api'
import { INTERVALS, SOURCE, movingAverage, toBars, type Interval } from './bars'
import { Candles, type PriceLine } from './Candles'
import { Chart } from './Chart'
import { CopyPage, addMoney, copyMenu, copyStockMenu, emptyText, sellOne, takeOut, useCopies } from './Copy'
import { Steps } from './icons'
import { Btn, I, Sheet, Status, Tabs, askSure, cx, menuOn, pct, toneOf, usd, type PopEntry } from './kit'
import { Bar, Empty, Page, ask, qtyText, useTabs } from './Page'
import { useDemo } from './store'
import { Ticket, tradeFromCopy, useTicket } from './Ticket'
import { starts } from './words'

const money = (n: number | null | undefined) => (n == null ? '—' : usd(n))
const num = (n: number, d = 4) => n.toLocaleString('en-US', { maximumFractionDigits: d })
const delta = (n: number | null | undefined, p?: number | null) => (n == null ? '—' : `${n >= 0 ? '▲' : '▼'} ${usd(Math.abs(n))}${p != null ? ` (${Math.abs(p).toFixed(2)}%)` : ''}`)
const SESSION: Record<string, string> = { pre: 'Pre-market', regular: 'Market open', after: 'After hours', closed: 'Market closed' }
const label = 'm-0 text-[12px] font-normal leading-4 tracking-normal text-ws-label'

/** The live picture, shared by the bar and the page (one stream while the portfolio is on screen). */
function usePortfolio() {
  const live = usePortfolioLive()
  const account = useTabs((s) => s.account)
  const scope = account === 'all' ? null : account
  const shown = useMemo(() => (live.portfolio ? scopePortfolio(live.portfolio, scope) : null), [live.portfolio, scope])
  return { ...live, all: live.portfolio, shown, scope }
}

export function PortfolioBar() {
  const t = useTabs()
  const symbol = useDemo((s) => s.symbol)
  const copyId = useDemo((s) => s.copyId)
  const openStock = useDemo((s) => s.openStock)
  const { all } = usePortfolio()
  if (symbol || copyId)
    return (
      <Bar
        right={
          symbol && (
            <Btn variant="white" className="xl:hidden" onClick={() => useTicket.getState().load(symbol)}>
              Trade
            </Btn>
          )
        }
      >
        <Btn icon={ArrowLeft} onClick={() => openStock(null)}>
          Portfolio
        </Btn>
      </Bar>
    )
  const accounts = all?.accounts ?? []
  return (
    <Bar
      right={
        all && (
          <span className="flex items-center gap-4 pr-1 text-[12px] tracking-normal text-ws-fg-3">
            <span className="hidden sm:block">{SESSION[all.session] ?? ''}</span>
            <Status tone={all.live ? 'free' : 'off'}>{all.live ? 'Live' : 'Recorded by hand'}</Status>
            {/* on a narrow screen the ticket is a sheet: this opens it */}
            <Btn variant="white" className="xl:hidden" onClick={() => useTicket.setState({ sheet: true })}>
              Trade
            </Btn>
          </span>
        )
      }
    >
      <Tabs options={[{ id: 'all', label: accounts.length > 1 ? 'All accounts' : 'Portfolio' }, ...(accounts.length > 1 ? accounts.map((a) => ({ id: a.id, label: `${a.name}${a.mask ? ` ··${a.mask.slice(-4)}` : ''}` })) : [])]} value={accounts.some((a) => a.id === t.account) ? t.account : 'all'} onChange={(v) => t.set({ account: v })} />
    </Bar>
  )
}

const RANGES = ['1D', '1W', '1M', '3M', 'ALL'] as const
type Range = (typeof RANGES)[number]

/** The big number, and the line under it: walk the line and the number follows. */
function Hero({ p, scope }: { p: PortfolioLive; scope: string | null }) {
  const [range, setRange] = useState<Range>('1M')
  const [snaps, setSnaps] = useState<PortfolioSnapshot[]>([])
  const [at, setAt] = useState<number | null>(null)
  useEffect(() => {
    let on = true
    api.portfolioHistory(range, scope ?? 'all').then((s) => on && setSnaps(s), () => on && setSnaps([]))
    return () => {
      on = false
    }
  }, [range, scope])
  // The line ends at what the portfolio is worth right now.
  const points = useMemo(() => [...snaps.map((s) => s.value), p.totals.value], [snaps, p.totals.value])
  const labels = useMemo(() => [...snaps.map((s) => new Date(s.ts).toLocaleString('en-US', range === '1D' ? { hour: 'numeric', minute: '2-digit' } : { month: 'short', day: 'numeric', hour: 'numeric' })), 'Now'], [snaps, range])
  const shown = at != null && points[at] != null ? points[at] : p.totals.value
  const since = points.length > 1 ? shown - points[0] : 0
  const sincePct = points.length > 1 && points[0] ? (since / points[0]) * 100 : 0
  const today = at == null
  return (
    <section>
      <h2 className={label}>{at != null ? labels[at] : 'Portfolio value'}</h2>
      <div className="mt-1 text-[40px] font-medium leading-[48px] tracking-[-0.02em] text-ws-fg tabular-nums">{usd(shown)}</div>
      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] tracking-normal">
        {today ? (
          <>
            <span className={toneOf(p.totals.dayPnl)}>
              {delta(p.totals.dayPnl, p.totals.dayPct)} <span className="text-ws-fg-3">today</span>
            </span>
            <span className={toneOf(p.totals.openPnl)}>
              {delta(p.totals.openPnl, p.totals.openPnlPct)} <span className="text-ws-fg-3">open</span>
            </span>
          </>
        ) : (
          <span className={toneOf(since)}>
            {delta(since, sincePct)} <span className="text-ws-fg-3">since the start of this stretch</span>
          </span>
        )}
      </div>
      {points.length > 2 ? <div className="mt-3">{<Chart points={points} labels={labels} height={220} bare live onScrub={setAt} />}</div> : <div className="mt-3 flex h-[120px] items-center text-[12px] tracking-normal text-ws-fg-4">The line appears once there is some history to draw.</div>}
      <div className="mt-1 flex h-9 items-center gap-1">
        {RANGES.map((r) => (
          <button key={r} type="button" aria-pressed={range === r} onClick={() => setRange(r)} className={cx('h-7 rounded-[6px] px-[10px] text-[12px] font-medium tracking-normal transition-colors duration-150', range === r ? (since >= 0 ? 'text-ws-up' : 'text-ws-down') : 'text-ws-fg-3 hover:text-ws-fg')}>
            {r}
          </button>
        ))}
      </div>
    </section>
  )
}

/* ── the accounts ────────────────────────────────────────────────────── */

/** Each account as its broker has it. Pressed, the page narrows to it; a third stands beside the two. */
function Accounts({ accounts, total }: { accounts: PfAccount[]; total: number }) {
  const t = useTabs()
  const three = accounts.length >= 3
  return (
    <div className={cx('-mx-3 grid border-y border-ws-edge', accounts.length > 1 && 'sm:grid-cols-2', three && 'xl:grid-cols-3')}>
      {accounts.map((a, i) => {
        const share = total ? (a.value / total) * 100 : 0
        const on = t.account === a.id
        const mode = !a.live ? 'By hand' : a.paper ? 'Paper' : a.tradable ? 'Trading' : 'Read only'
        // the lines between them, whichever way they fall into rows
        const lines = cx(i > 0 && 'max-sm:border-t', i % 2 === 1 && 'sm:max-xl:border-l', i >= 2 && 'sm:max-xl:border-t', three ? cx(i % 3 !== 0 && 'xl:border-l', i >= 3 && 'xl:border-t') : cx(i % 2 === 1 && 'xl:border-l', i >= 2 && 'xl:border-t'))
        return (
          <button key={a.id} type="button" aria-pressed={on} title={a.blocked ?? undefined} onClick={() => accounts.length > 1 && t.set({ account: on ? 'all' : a.id })} className={cx('block border-ws-edge px-3 py-3 text-left transition-colors duration-150 hover:bg-ws-hover', lines, on && 'bg-ws-hover')}>
            <span className="flex h-5 items-center gap-2">
              <span className={cx('block h-[6px] w-[6px] shrink-0 rounded-full', a.live ? 'bg-ws-up' : 'bg-ws-fg-5')} />
              <span className="truncate font-medium text-ws-fg">{a.name}</span>
              {a.mask && <span className="shrink-0 text-[12px] tracking-normal text-ws-fg-4">··{a.mask.slice(-4)}</span>}
              <span className="ml-auto shrink-0 text-[12px] tracking-normal text-ws-fg-3">{mode}</span>
            </span>
            <span className="mt-2 flex items-baseline justify-between gap-3">
              <span className="text-[18px] font-medium leading-6 tracking-[-0.015em] tabular-nums text-ws-fg">{usd(a.value)}</span>
              {a.dayPnl != null && (
                <span className={cx('text-[12px] tracking-normal tabular-nums', toneOf(a.dayPnl))}>
                  {signedUsd(a.dayPnl)} <span className="text-ws-fg-3">today</span>
                </span>
              )}
            </span>
            <span className="mt-2 block h-[2px] bg-ws-edge">
              <span className="block h-full bg-ws-fg" style={{ width: `${Math.max(0, Math.min(100, share))}%` }} />
            </span>
            <span className="mt-2 flex h-4 items-center justify-between text-[12px] leading-4 tracking-normal text-ws-fg-3">
              <span>{share.toFixed(0)}% of the total</span>
              {(a.tradable ? (a.buyingPower ?? a.cash) : a.cash) != null && (
                <span>
                  {a.tradable && a.buyingPower != null ? 'Buying power' : 'Cash'} <span className="tabular-nums text-ws-fg-2">{usd((a.tradable ? (a.buyingPower ?? a.cash) : a.cash)!)}</span>
                </span>
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/* ── what is held, line by line ──────────────────────────────────────── */

/** One line of the table: a stock in one account, without the shares that belong to a copy. */
interface Held {
  key: string
  symbol: string
  code: string
  account?: PfAccount
  where: string
  tradable: boolean
  qty: number
  avg: number | null
  last: number | null
  value: number
  open: number | null
  openPct: number | null
  day: number | null
  dayPct: number | null
  stop: number | null
  target: number | null
  worker: { id: string; name: string } | null
}
/** What an account is called in a row: the part of its name that tells it from the others. */
function whereOf(a: PfAccount | undefined, all: PfAccount[]): string {
  if (!a) return ''
  const own = a.name.split(' · ')[1]
  const name = own ?? (all.filter((x) => x.broker === a.broker).length > 1 ? 'Main' : a.name)
  return a.broker === 'manual' ? 'Recorded by hand' : a.tradable ? name : `${name} · read only`
}
function heldOf(p: PortfolioLive, all: PortfolioLive, copies: CopyView[]): Held[] {
  const out: Held[] = []
  for (const h of p.holdings) {
    for (const part of h.parts) {
      const copied = part.copyQty ?? 0
      const qty = part.quantity - copied
      if (qty <= 1e-9) continue
      const share = part.quantity ? qty / part.quantity : 1
      const account = all.accounts.find((a) => a.id === part.account)
      const value = part.value != null ? part.value * share : h.last != null ? h.last * qty : 0
      // a copy keeps its own averages: what it paid is taken out of the broker's blended figure
      const copyCost = copied > 0 ? copies.reduce((a, c) => a + (c.positions.find((x) => x.symbol === h.symbol)?.avg ?? 0) * (c.positions.find((x) => x.symbol === h.symbol)?.qty ?? 0), 0) : 0
      const cost = part.avgCost != null ? part.avgCost * part.quantity - copyCost : null
      const open = copied > 0 ? (cost != null ? value - cost : null) : part.openPnl
      const day = part.dayPnl != null ? part.dayPnl * share : null
      out.push({
        key: `${part.account}:${h.code}`,
        symbol: h.symbol,
        code: h.code,
        account,
        where: whereOf(account, all.accounts),
        tradable: account?.tradable !== false,
        qty,
        avg: cost != null && qty ? cost / qty : part.avgCost,
        last: h.last,
        value,
        open,
        openPct: open != null && cost ? (open / cost) * 100 : null,
        day,
        dayPct: day != null && value - day > 0 ? (day / (value - day)) * 100 : null,
        stop: part.workers.length || h.parts.length === 1 ? h.stopLoss : null,
        target: part.workers.length || h.parts.length === 1 ? h.takeProfit : null,
        worker: part.workers[0] ?? null,
      })
    }
  }
  return out.sort((a, b) => b.value - a.value)
}

/* ── the allocation ──────────────────────────────────────────────────── */

/** One colour a holding — the colours of an agent's face, so the bar belongs to this screen. */
const HUE = ['#9fe8c8', '#aec2f8', '#bfaef8', '#ee82b4', '#ffd29a', '#ffeaa6']

function Allocation({ list, copies, cash, value }: { list: Held[]; copies: CopyView[]; cash: number; value: number }) {
  const openStock = useDemo((s) => s.openStock)
  const openCopy = useDemo((s) => s.openCopy)
  const by = new Map<string, { symbol: string; v: number; code?: string; copy?: string }>()
  for (const h of list) by.set(h.symbol, { symbol: h.symbol, code: h.code, v: (by.get(h.symbol)?.v ?? 0) + h.value })
  // a copy is one piece of the whole, whatever it holds inside
  for (const c of copies) by.set(c.id, { symbol: `Copy · ${c.leader.name}`, v: c.value, copy: c.id })
  const sorted = [...by.values()].filter((x) => x.v > 0).sort((a, b) => b.v - a.v)
  const rest = sorted.slice(5).reduce((a, x) => a + x.v, 0)
  const rows: { symbol: string; v: number; color: string; code?: string; copy?: string }[] = [...sorted.slice(0, 5).map((x, i) => ({ ...x, color: HUE[i] })), ...(rest > 0 ? [{ symbol: 'Other', v: rest, color: HUE[5] }] : []), ...(cash > 0 ? [{ symbol: 'Cash', v: cash, color: '#3a3a3a' }] : [])]
  const whole = rows.reduce((a, r) => a + r.v, 0) || value || 1
  if (!rows.length) return null
  return (
    <div>
      <div className="flex h-2 gap-[2px]">
        {rows.map((r) => (
          <span key={r.symbol} className="h-full first:rounded-l-[2px] last:rounded-r-[2px]" style={{ width: `${(r.v / whole) * 100}%`, background: r.color }} />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1">
        {rows.map((r) => (
          <button key={r.symbol} type="button" disabled={!r.code && !r.copy} title={usd(r.v)} onClick={() => (r.copy ? openCopy(r.copy) : r.code && openStock(r.code))} {...(r.code ? menuOn(() => stockMenu(r.code!)) : {})} className="flex h-5 items-center gap-2 text-[12px] tracking-normal">
            <span className="block h-2 w-2 shrink-0 rounded-[2px]" style={{ background: r.color }} />
            <span className="font-medium text-ws-fg">{r.symbol}</span>
            <span className="tabular-nums text-ws-fg-3">{((r.v / whole) * 100).toFixed(1)}%</span>
          </button>
        ))}
      </div>
    </div>
  )
}

/* ── holdings · orders · activity ────────────────────────────────────── */

const signedUsd = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd(Math.abs(n))}`
const HCOLS = 'lg:grid-cols-[minmax(124px,1.3fr)_minmax(64px,1fr)_minmax(60px,0.9fr)_minmax(60px,0.9fr)_minmax(72px,1fr)_minmax(80px,1.1fr)_minmax(72px,1fr)_minmax(78px,1fr)_40px]'
const two = 'hidden text-right tabular-nums lg:block'
const lower = 'block text-[12px] leading-4'
const quiet = 'rounded-[6px] px-2 py-1 text-[12px] tracking-normal text-ws-fg-3 transition-colors duration-150 hover:bg-ws-raise hover:text-ws-fg'
const none = <span className={cx(two, 'text-ws-fg-5')}>—</span>
const ic = (icon: typeof ChartLine) => <I icon={icon} className="text-ws-icon" />

/** What a right-click on a stock offers. */
function stockMenu(code: string): PopEntry[] {
  const s = useDemo.getState()
  const symbol = code.split('.')[0]
  return [
    { label: 'Trade', onSelect: () => useTicket.getState().load(code) },
    'rule' as const,
    { label: 'Open chart', icon: ic(ChartLine), onSelect: () => s.openStock(code) },
    { label: `Ask about ${symbol}`, icon: ic(MessagesSquare), onSelect: () => ask(null, starts.aboutStock(symbol)) },
    { label: 'Copy symbol', icon: ic(CopyIcon), onSelect: () => (void navigator.clipboard?.writeText(symbol).catch(() => undefined), s.say(`${symbol} copied`)) },
  ]
}

function HoldingRow({ h, weight }: { h: Held; weight: number }) {
  const chosen = useTicket((s) => h.tradable && s.symbol === h.code)
  return (
    <button type="button" title={h.tradable ? undefined : 'Read only: this account cannot be traded from here'} onClick={() => (h.tradable ? useTicket.getState().load(h.code) : useDemo.getState().openStock(h.code))} onDoubleClick={() => useDemo.getState().openStock(h.code)} {...menuOn(() => stockMenu(h.code))} className={cx('grid h-[52px] w-full grid-cols-[1fr_auto] items-center gap-x-3 px-3 text-left text-[13px] tracking-normal transition-colors duration-150 hover:bg-ws-hover', HCOLS, chosen && 'bg-ws-hover')}>
      <span className="min-w-0">
        <span className="block truncate text-[14px] font-medium leading-[18px] tracking-[-0.01em] text-ws-fg">{h.symbol}</span>
        <span className={cx(lower, 'truncate text-ws-fg-3')}>{h.where}</span>
      </span>
      <span className={cx(two, 'text-ws-fg-2')}>{qtyText(Number(h.qty.toFixed(6)))}</span>
      <span className={cx(two, 'text-ws-fg-2')}>{h.avg?.toFixed(2) ?? '—'}</span>
      <span className={cx(two, 'text-ws-fg')}>{h.last?.toFixed(2) ?? '—'}</span>
      {h.day != null ? (
        <span className={cx(two, toneOf(h.day))}>
          {signedUsd(h.day)}
          {h.dayPct != null && <span className={lower}>{pct(h.dayPct)}</span>}
        </span>
      ) : (
        none
      )}
      <span className={cx(two, 'text-ws-fg')}>{usd(h.value)}</span>
      {h.open != null ? (
        <span className={cx(two, toneOf(h.open))}>
          {signedUsd(h.open)}
          {h.openPct != null && <span className={lower}>{pct(h.openPct)}</span>}
        </span>
      ) : (
        none
      )}
      <span className={cx(two, 'text-[12px] leading-4')}>
        {h.stop != null || h.target != null ? (
          <>
            {h.stop != null && (
              <span className="block text-ws-fg-2">
                <span className="text-ws-fg-4">SL</span> {h.stop.toFixed(2)}
              </span>
            )}
            {h.target != null && (
              <span className="block text-ws-fg-2">
                <span className="text-ws-fg-4">TP</span> {h.target.toFixed(2)}
              </span>
            )}
          </>
        ) : (
          <span className="text-ws-fg-5">—</span>
        )}
      </span>
      <span className={cx(two, 'text-ws-fg-3')}>{weight.toFixed(0)}%</span>
      {/* on a phone: what it is worth and how it moved today */}
      <span className="text-right tabular-nums lg:hidden">
        <span className="block text-[14px] leading-[18px] text-ws-fg">{usd(h.value)}</span>
        {h.dayPct != null && <span className={cx(lower, toneOf(h.dayPct))}>{pct(h.dayPct)}</span>}
      </span>
    </button>
  )
}

/**
 * A copy among the holdings: one row. Pressed, it opens the copy's own page; its arrow opens it in place
 * to what it holds; a right-click offers what can be done with it.
 */
function CopyRow({ c, total, where, onChanged }: { c: CopyView; total: number; where: string; onChanged: () => void }) {
  const [open, setOpen] = useState(false)
  const openCopy = useDemo((s) => s.openCopy)
  const openStock = useDemo((s) => s.openStock)
  const chosen = useTicket((s) => (s.account === `copy:${c.id}` ? s.symbol : null))
  const live = c.status === 'active' || c.status === 'paused'
  return (
    <div>
      <div role="button" tabIndex={0} onClick={() => openCopy(c.id)} onKeyDown={(e) => e.key === 'Enter' && openCopy(c.id)} {...menuOn(() => copyMenu(c, onChanged))} className={cx('grid h-[52px] w-full cursor-pointer grid-cols-[1fr_auto] items-center gap-x-3 px-3 text-left text-[13px] tracking-normal transition-colors duration-150 hover:bg-ws-hover', HCOLS, open && 'bg-ws-hover')}>
        <span className="flex min-w-0 items-center gap-1">
          <button type="button" aria-expanded={open} aria-label={open ? 'Close what it holds' : 'Show what it holds'} title={open ? 'Close' : 'Show what it holds'} onClick={(e) => (e.stopPropagation(), setOpen(!open))} onDoubleClick={(e) => e.stopPropagation()} className="-ml-[6px] flex h-7 w-5 shrink-0 items-center justify-center rounded-[6px] text-ws-fg-4 transition-colors duration-150 hover:bg-ws-raise hover:text-ws-fg">
            <ChevronRight size={13} strokeWidth={2} className={cx('transition-transform duration-150', open && 'rotate-90')} />
          </button>
          <span className="min-w-0">
            <span className="block truncate text-[14px] font-medium leading-[18px] tracking-[-0.01em] text-ws-fg">Copy · {c.leader.name}</span>
            <span className={cx(lower, 'truncate text-ws-fg-3')}>
              {where ? `${where} · ` : ''}
              {c.positions.length} {c.positions.length === 1 ? 'stock' : 'stocks'}
              {c.live ? '' : ' · rehearsal'}
            </span>
          </span>
        </span>
        {none}
        {none}
        {none}
        <span className={cx(two, toneOf(c.day))}>
          {signedUsd(c.day)}
          <span className={lower}>{pct(c.dayPct)}</span>
        </span>
        <span className={cx(two, 'text-ws-fg')}>{usd(c.value)}</span>
        <span className={cx(two, toneOf(c.gain))}>
          {signedUsd(c.gain)}
          <span className={lower}>{pct(c.gainPct)}</span>
        </span>
        {none}
        <span className={cx(two, 'text-ws-fg-3')}>{total ? ((c.value / total) * 100).toFixed(0) : 0}%</span>
        <span className="text-right tabular-nums lg:hidden">
          <span className="block text-[14px] leading-[18px] text-ws-fg">{usd(c.value)}</span>
          <span className={cx(lower, toneOf(c.gain))}>{pct(c.gainPct)}</span>
        </span>
      </div>
      {open && (
        <div className="border-t border-ws-edge bg-ws-well">
          {c.positions.map((p) => {
            const code = `${p.symbol}.US`
            // pressed: the ticket sells this copy's own shares of it; twice: its chart
            return (
              <div key={p.symbol} role="button" tabIndex={0} onClick={() => (live ? tradeFromCopy(c.id, p.symbol) : openStock(p.symbol))} onDoubleClick={() => openStock(p.symbol)} onKeyDown={(e) => e.key === 'Enter' && openStock(p.symbol)} {...menuOn(() => copyStockMenu(c, p.symbol, onChanged))} className={cx('grid h-12 cursor-pointer grid-cols-[1fr_auto] items-center gap-x-3 border-b border-ws-hair px-3 text-[13px] tracking-normal transition-colors duration-150 hover:bg-ws-hover', HCOLS, chosen === code && 'bg-ws-hover')}>
                <span className="truncate pl-[18px] font-medium text-ws-fg">
                  {p.symbol} <span className="font-normal text-ws-fg-3">{p.name}</span>
                </span>
                <span className={cx(two, 'text-ws-fg-2')}>{qtyText(Number(p.qty.toFixed(6)))}</span>
                <span className={cx(two, 'text-ws-fg-2')}>{p.avg.toFixed(2)}</span>
                <span className={cx(two, 'text-ws-fg')}>{p.last.toFixed(2)}</span>
                <span className={cx(two, toneOf(p.day))}>{signedUsd(p.day)}</span>
                <span className={cx(two, 'text-ws-fg')}>{usd(p.value)}</span>
                <span className={cx(two, toneOf(p.gain))}>
                  {signedUsd(p.gain)}
                  <span className={lower}>{pct(p.gainPct)}</span>
                </span>
                <span className="hidden justify-end lg:flex">
                  <button type="button" disabled={c.status === 'stopping'} onClick={(e) => (e.stopPropagation(), sellOne(c, p.symbol, onChanged))} onDoubleClick={(e) => e.stopPropagation()} className={cx(quiet, '-mr-2')}>
                    Sell
                  </button>
                </span>
                <span className={cx(two, 'text-ws-fg-3')}>{c.value ? ((p.value / c.value) * 100).toFixed(0) : 0}%</span>
                <span className="text-right tabular-nums text-ws-fg lg:hidden">{usd(p.value)}</span>
              </div>
            )
          })}
          {!c.positions.length && <div className="border-b border-ws-hair px-3 py-3 pl-[30px] text-[12px] tracking-normal text-ws-fg-3">{emptyText(c)}</div>}
          <div className="flex min-h-11 flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 pl-[30px] text-[12px] tracking-normal text-ws-fg-3">
            <span>
              Put in <span className="tabular-nums text-ws-fg-2">{usd(c.putIn)}</span>
            </span>
            <span>
              Cash inside <span className="tabular-nums text-ws-fg-2">{usd(c.cash)}</span>
            </span>
            <span>Since {new Date(c.since).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
            <span className="ml-auto flex items-center gap-1">
              <button type="button" onClick={() => addMoney(c, onChanged)} className={quiet}>
                Add money
              </button>
              <button type="button" disabled={c.cash < 0.01} onClick={() => takeOut(c, onChanged)} className={quiet}>
                Take out
              </button>
              <button type="button" onClick={() => openCopy(c.id)} className={cx(quiet, 'text-ws-fg')}>
                Open
              </button>
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

const KIND: Record<string, string> = { MKT: 'market', LMT: 'limit', STP: 'stop', 'STP LMT': 'stop limit', TRAIL: 'trailing stop' }
const orderWords = (o: PfOrder) => `${KIND[o.type] ?? o.type.toLowerCase()}${o.limit != null ? ` ${o.limit.toFixed(2)}` : ''}${o.stop != null ? `${o.limit != null ? ' · stop' : ''} ${o.stop.toFixed(2)}` : ''}${o.tif ? ` · ${o.tif === 'GTC' ? 'until cancelled' : o.tif === 'DAY' ? 'day' : o.tif.toLowerCase()}` : ''}`

function Orders({ p, all, onChanged }: { p: PortfolioLive; all: PortfolioLive; onChanged: () => void }) {
  const say = useDemo((s) => s.say)
  if (!p.orders.length) return <p className="m-0 px-3 py-8 text-center text-[12px] tracking-normal text-ws-fg-3">No working orders.</p>
  const row = 'grid min-h-[52px] grid-cols-[1fr_auto] items-center gap-x-3 px-3 py-2 text-[13px] tracking-normal sm:grid-cols-[minmax(150px,1.3fr)_minmax(130px,1fr)_minmax(120px,1fr)_152px]'
  const source = 'hidden min-w-0 items-center gap-[6px] truncate text-left text-[12px] text-ws-fg-3 transition-colors hover:text-ws-fg sm:flex'
  return (
    <div className="divide-y divide-ws-edge">
      {p.orders.map((o) => {
        const account = all.accounts.find((a) => a.id === o.account)
        return (
          <div key={o.id} className={row}>
            <span className="min-w-0">
              <span className="block truncate text-[14px] font-medium leading-[18px] tracking-[-0.01em] text-ws-fg">
                <span className={o.side === 'SELL' ? 'text-ws-down' : 'text-ws-up'}>{o.side}</span> {o.quantity != null ? qtyText(Number(o.quantity.toFixed(6))) : ''} {o.symbol}
              </span>
              <span className={cx(lower, 'truncate text-ws-fg-3')}>
                {orderWords(o)}
                {o.filled ? ` · ${qtyText(Number(o.filled.toFixed(6)))} filled` : ''}
              </span>
            </span>
            <Status tone="free" className="hidden sm:inline-flex">
              At {account?.name.split(' · ')[0] ?? 'the broker'}
            </Status>
            {o.workerId ? (
              <button type="button" onClick={() => useDemo.setState({ view: 'workers', workerId: o.workerId })} className={source}>
                <Steps size={14} />
                <span className="truncate">A worker’s</span>
              </button>
            ) : (
              <span className={cx(source, 'hover:text-ws-fg-3')}>{o.ticketId ? (o.fromChat ? 'From a conversation' : 'From the ticket') : 'Placed at the broker'}</span>
            )}
            {o.orderId != null && !o.workerId ? (
              <button type="button" aria-label="Cancel order" title="Cancel order" onClick={() => askSure({ title: 'Cancel this order?', body: `${o.side} ${o.quantity ?? ''} ${o.symbol} · ${orderWords(o)}`, cta: 'Cancel order', done: () => void brokerApi.cancelOrder(o.orderId!).then(() => (say('Cancel sent'), onChanged()), (e) => say(e instanceof Error ? e.message : 'It could not be cancelled')) })} className="ml-auto flex h-7 w-7 items-center justify-center rounded-[6px] text-ws-fg-4 transition-colors duration-150 hover:bg-ws-raise hover:text-ws-down">
                <X size={14} strokeWidth={2} />
              </button>
            ) : (
              <span />
            )}
          </div>
        )
      })}
    </div>
  )
}

const SOURCE_WORDS: Record<string, string> = { worker: 'A worker', order: 'A conversation', app: 'The ticket', broker: 'At the broker', recorded: 'Recorded by hand' }
function Activity({ p, all }: { p: PortfolioLive; all: PortfolioLive }) {
  if (!p.fills.length) return <p className="m-0 px-3 py-8 text-center text-[12px] tracking-normal text-ws-fg-3">No trades yet.</p>
  const fills = p.fills.slice(0, 120)
  const group = (title: string, list: typeof fills) =>
    list.length > 0 && (
      <div>
        <div className="flex h-8 items-end px-3 pb-1 text-[12px] tracking-normal text-ws-fg-4">{title}</div>
        <div className="divide-y divide-ws-edge border-y border-ws-edge">
          {list.map((f) => (
            <div key={f.id} className="grid h-11 grid-cols-[1fr_auto] items-center gap-x-3 px-3 text-[13px] tracking-normal sm:grid-cols-[112px_minmax(0,1.4fr)_minmax(0,1fr)_96px]">
              <span className="hidden text-[12px] text-ws-fg-3 sm:block">{new Date(f.at).toLocaleString('en-US', f.today ? { hour: 'numeric', minute: '2-digit' } : { month: 'short', day: 'numeric' })}</span>
              <span className="min-w-0 truncate text-ws-fg">
                <span className={f.side === 'SELL' ? 'text-ws-down' : 'text-ws-up'}>{f.side === 'SELL' ? 'Sold' : 'Bought'}</span> {qtyText(Number(f.shares.toFixed(6)))} {f.symbol} <span className="tabular-nums text-ws-fg-3">at {f.price.toFixed(2)}</span>
              </span>
              <span className="hidden min-w-0 truncate text-[12px] text-ws-fg-3 sm:block">
                {SOURCE_WORDS[f.source] ?? f.source} · {whereOf(all.accounts.find((a) => a.id === f.account), all.accounts).replace(' · read only', '')}
              </span>
              <span className="text-right tabular-nums text-ws-fg-2">{usd(f.shares * f.price)}</span>
            </div>
          ))}
        </div>
      </div>
    )
  return (
    <div className="[&>div:last-child>div:last-child]:border-b-0">
      {group('Today', fills.filter((f) => f.today))}
      {group('Earlier', fills.filter((f) => !f.today))}
    </div>
  )
}

function Overview({ all, shown, scope, refresh }: { all: PortfolioLive; shown: PortfolioLive; scope: string | null; refresh: () => void }) {
  const [tab, setTab] = useState<'holdings' | 'orders' | 'activity'>('holdings')
  const wide = useMedia('(min-width: 1280px)')
  const sheet = useTicket((s) => s.sheet)
  const { data: copyData, load: reloadCopies } = useCopies()
  // a copy lives in the account its broker trades in: it is listed when the page shows that account
  const account = scope ? all.accounts.find((a) => a.id === scope) : undefined
  const copies = (copyData?.copies ?? []).filter((c) => c.status !== 'stopped' && (!account || (account.broker === c.broker && account.tradable)))
  const list = heldOf(shown, all, copyData?.copies ?? [])
  const total = shown.totals.value
  // (a copy's money is counted once: inside its own row — the cash row is what is free outside the copies)
  const cash = Math.max(0, shown.totals.cash - copies.reduce((a, c) => a + Math.max(0, c.cash), 0))
  const copyWhere = (c: CopyView) => whereOf(all.accounts.find((a) => a.broker === c.broker && a.tradable), all.accounts)
  const th = 'hidden text-right lg:block'
  const changed = () => (refresh(), reloadCopies())
  return (
    <Page className="mx-auto max-w-[1360px]">
      <div className="grid items-start gap-x-8 gap-y-3 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0">
          <Hero p={shown} scope={scope} />

          <div className="mt-6">
            <Accounts accounts={all.accounts} total={all.totals.value} />
          </div>
          <div className="mt-5">
            <Allocation list={list} copies={copies} cash={cash} value={total} />
          </div>

          <div className="-mx-3 mt-8">
            <div className="flex h-12 items-center border-b border-ws-edge px-1">
              <Tabs
                options={[
                  { id: 'holdings', label: 'Holdings', count: list.length + copies.length },
                  { id: 'orders', label: 'Orders', count: shown.orders.length || undefined },
                  { id: 'activity', label: 'Activity' },
                ]}
                value={tab}
                onChange={setTab}
              />
              <Btn icon={Plus} className="ml-auto" title="A trade made somewhere else: it is written down, no order is sent" onClick={() => useTicket.setState({ account: 'record', sheet: true })}>
                <span className="hidden sm:inline">Record a holding</span>
              </Btn>
            </div>
            {tab === 'holdings' && (
              <>
                <div className={cx('grid h-9 grid-cols-[1fr_auto] items-center gap-x-3 border-b border-ws-edge px-3 text-[12px] tracking-normal text-ws-fg-3', HCOLS)}>
                  <span>Symbol</span>
                  <span className={th}>Qty</span>
                  <span className={th}>Avg cost</span>
                  <span className={th}>Last</span>
                  <span className={th}>Today</span>
                  <span className={th}>Value</span>
                  <span className={th}>Open profit</span>
                  <span className={th}>SL / TP</span>
                  <span className={th}>Wt</span>
                  <span className="lg:hidden">Value</span>
                </div>
                <div className="divide-y divide-ws-edge border-b border-ws-edge">
                  {list.map((h) => (
                    <HoldingRow key={h.key} h={h} weight={total ? (h.value / total) * 100 : 0} />
                  ))}
                  {copies.map((c) => (
                    <CopyRow key={c.id} c={c} total={total} where={copyWhere(c)} onChanged={changed} />
                  ))}
                  <div className={cx('grid h-11 grid-cols-[1fr_auto] items-center gap-x-3 px-3 text-[13px] tracking-normal', HCOLS)}>
                    <span className="text-ws-fg-2">Cash</span>
                    <span className="text-right tabular-nums text-ws-fg lg:col-start-6">{usd(cash)}</span>
                    <span className={cx(two, 'text-ws-fg-3 lg:col-start-9')}>{total ? ((cash / total) * 100).toFixed(0) : 0}%</span>
                  </div>
                </div>
              </>
            )}
            {tab === 'orders' && <Orders p={shown} all={all} onChanged={refresh} />}
            {tab === 'activity' && <Activity p={shown} all={all} />}
          </div>
        </div>
        {wide && (
          <div className="ws-noscroll sticky top-5 max-h-[calc(100dvh-136px)] overflow-y-auto rounded-[10px]">
            <Ticket all={all} scope={scope} onChanged={changed} />
          </div>
        )}
      </div>
      {!wide && (
        <Sheet open={sheet} side="bottom" onClose={() => useTicket.setState({ sheet: false })} className="max-h-[92dvh] overflow-y-auto p-3 pb-[max(12px,env(safe-area-inset-bottom))]">
          <div className="mx-auto max-w-[440px]">
            <Ticket all={all} scope={scope} onChanged={changed} />
          </div>
        </Sheet>
      )}
    </Page>
  )
}

/* ── one stock ───────────────────────────────────────────────────────── */

const CALL: Record<string, string> = { 'strong-buy': 'Strong buy', buy: 'Buy', neutral: 'Neutral', sell: 'Sell', 'strong-sell': 'Strong sell' }
const callTone = (c: string) => (c.includes('buy') ? 'text-ws-up' : c.includes('sell') ? 'text-ws-down' : 'text-ws-fg-2')
const big = (n: number | null) => (n == null ? '—' : n >= 1e12 ? `$${(n / 1e12).toFixed(2)}T` : n >= 1e9 ? `$${(n / 1e9).toFixed(2)}B` : n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : usd(n))
const Fact = ({ k, children }: { k: string; children: ReactNode }) => (
  <div className="flex min-h-9 items-center justify-between gap-3 text-[13px] tracking-normal">
    <dt className="shrink-0 text-ws-fg-3">{k}</dt>
    <dd className="m-0 truncate text-right text-ws-fg-2 tabular-nums">{children}</dd>
  </div>
)

function Stock({ code, all, scope, refresh }: { code: string; all: PortfolioLive; scope: string | null; refresh: () => void }) {
  const symbol = code.split('.')[0]
  const h = all.holdings.find((x) => x.code === code || x.symbol === symbol)
  const [interval, setIv] = useState<Interval>('1D')
  const [layers, setLayers] = useState({ volume: true, rsi: false, ma20: false, ma50: true, ma200: false })
  const [raw, setRaw] = useState<OhlcBar[] | null>(null)
  const [ta, setTa] = useState<TechnicalAnalysis | null>(null)
  const [co, setCo] = useState<CompanyProfile | null>(null)
  const [quote, setQuote] = useState<{ price: number; change: number | null; changePct: number | null } | null>(null)
  useEffect(() => {
    let on = true
    setRaw(null)
    api.ohlc(code, SOURCE[interval].timeframe as never).then((b) => on && setRaw(b), () => on && setRaw([]))
    return () => {
      on = false
    }
  }, [code, interval])
  useEffect(() => {
    let on = true
    setTa(null)
    setCo(null)
    void api.technicals(code).then((x) => on && setTa(x), () => {})
    void api.profile(code).then((x) => on && setCo(x), () => {})
    void api.quote(code).then((q) => on && setQuote({ price: (q as { price?: number; close?: number }).price ?? (q as { close?: number }).close ?? 0, change: (q as { change?: number }).change ?? null, changePct: (q as { changePercent?: number; changePct?: number }).changePercent ?? (q as { changePct?: number }).changePct ?? null }), () => {})
    return () => {
      on = false
    }
  }, [code])
  // The ticket beside the chart is about this stock (on a narrow screen it is a sheet, opened by Trade).
  const wide = useMedia('(min-width: 1280px)')
  const sheet = useTicket((s) => s.sheet)
  useEffect(() => useTicket.setState({ symbol: code }), [code])
  const bars = useMemo(() => (raw && raw.length ? toBars(raw, SOURCE[interval].group) : null), [raw, interval])
  const last = h?.last ?? quote?.price ?? bars?.c[bars.c.length - 1] ?? null
  const dayPct = h?.dayPct ?? quote?.changePct ?? null
  // What is yours and what belongs to a copy are told apart: a copy keeps its own shares and its own average.
  const inCopies = (all.copies ?? []).flatMap((c) => c.positions.filter((p) => p.symbol.replace(/[\s.-]/g, '') === symbol.replace(/[\s.-]/g, '')).map((p) => ({ c, p })))
  const copied = inCopies.reduce((a, x) => a + x.p.qty, 0)
  const ownQty = h ? Math.max(0, h.quantity - copied) : 0
  const ownCost = h?.avgCost != null ? h.avgCost * h.quantity - inCopies.reduce((a, x) => a + x.p.avg * x.p.qty, 0) : null
  const own = h && ownQty > 1e-9 ? { qty: ownQty, avg: ownCost != null ? ownCost / ownQty : null, value: last != null ? last * ownQty : null, gain: ownCost != null && last != null ? last * ownQty - ownCost : null, cost: ownCost } : null
  // Your own lines: where you stand, the notes you keep, the orders resting at the broker.
  const lines: PriceLine[] = []
  if (own?.avg) lines.push({ y: own.avg, tone: 'pos', chip: `${num(own.qty)} sh · avg ${usd(own.avg)}`, more: own.gain != null ? `${own.gain >= 0 ? '+' : '−'}${usd(Math.abs(own.gain))}${own.cost ? ` (${pct((own.gain / own.cost) * 100)})` : ''}` : undefined })
  for (const { c, p } of inCopies) lines.push({ y: p.avg, tone: 'pos', chip: `Copy · ${c.leader.name} ${qtyText(Number(p.qty.toFixed(6)))} sh · avg ${usd(p.avg)}`, more: `${p.gain >= 0 ? '+' : '−'}${usd(Math.abs(p.gain))} (${pct(p.gainPct)})` })
  for (const o of all.orders.filter((x) => x.symbol === symbol)) {
    if (o.stop != null) lines.push({ y: o.stop, tone: o.side === 'SELL' ? 'down' : 'pos', chip: `${o.side === 'SELL' ? 'Stop' : 'Buy stop'} ${usd(o.stop)}`, more: `${o.quantity != null ? num(o.quantity) : ''} sh · ${o.status}` })
    else if (o.limit != null) lines.push({ y: o.limit, tone: o.side === 'SELL' ? 'up' : 'pos', chip: `${o.side === 'SELL' ? 'Sell' : 'Buy'} limit ${usd(o.limit)}`, more: `${o.quantity != null ? num(o.quantity) : ''} sh · ${o.status}` })
  }
  if (h?.stopLoss && !lines.some((l) => Math.abs(l.y - h.stopLoss!) < 0.005)) lines.push({ y: h.stopLoss, tone: 'down', chip: `Stop note ${usd(h.stopLoss)}` })
  if (h?.takeProfit && !lines.some((l) => Math.abs(l.y - h.takeProfit!) < 0.005)) lines.push({ y: h.takeProfit, tone: 'up', chip: `Target note ${usd(h.takeProfit)}` })
  const overlays = bars ? [...(layers.ma20 ? [{ points: movingAverage(bars.c, 20), label: 'MA 20' }] : []), ...(layers.ma50 ? [{ points: movingAverage(bars.c, 50), label: 'MA 50' }] : []), ...(layers.ma200 ? [{ points: movingAverage(bars.c, 200), label: 'MA 200' }] : [])] : []
  const layer = (k: keyof typeof layers, text: string) => (
    <button key={k} type="button" aria-pressed={layers[k]} onClick={() => setLayers({ ...layers, [k]: !layers[k] })} className={cx('h-7 rounded-[6px] px-2 text-[12px] tracking-normal transition-colors duration-150', layers[k] ? 'bg-ws-panel text-ws-fg' : 'text-ws-fg-3 hover:text-ws-fg')}>
      {text}
    </button>
  )
  return (
    <Page>
      <div className="flex flex-col gap-8 xl:flex-row xl:gap-10">
        <div className="min-w-0 flex-1 space-y-7">
          <section>
            <div className="flex items-baseline gap-3">
              <h1 className="m-0 text-[20px] font-medium leading-7 tracking-[-0.01em] text-ws-fg">{symbol}</h1>
              <span className="truncate text-[13px] tracking-normal text-ws-fg-3">{co?.name ?? ''}</span>
              <button type="button" onClick={() => ask(null, `$${symbol} `)} className="ml-auto flex shrink-0 items-center gap-2 rounded-[8px] px-2 py-1 text-[12px] tracking-normal text-ws-fg-3 transition-colors hover:bg-ws-hover hover:text-ws-fg">
                <MessagesSquare size={14} strokeWidth={1.8} />
                Ask about it
              </button>
            </div>
            <div className="mt-1 flex items-baseline gap-3">
              <span className="text-[32px] font-medium leading-10 tracking-[-0.02em] text-ws-fg tabular-nums">{last != null ? usd(last) : '—'}</span>
              {dayPct != null && <span className={cx('text-[13px] tracking-normal', toneOf(dayPct))}>{pct(dayPct)} today</span>}
            </div>
          </section>

          <section>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <div className="flex items-center gap-px">
                {INTERVALS.map((i) => (
                  <button key={i} type="button" aria-pressed={interval === i} onClick={() => setIv(i)} className={cx('h-7 rounded-[6px] px-2 text-[12px] font-medium tracking-normal transition-colors duration-150', interval === i ? 'bg-ws-panel text-ws-fg' : 'text-ws-fg-3 hover:text-ws-fg')}>
                    {i}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-px xl:ml-auto">
                {layer('volume', 'Volume')}
                {layer('rsi', 'RSI')}
                {layer('ma20', 'MA 20')}
                {layer('ma50', 'MA 50')}
                {layer('ma200', 'MA 200')}
              </div>
            </div>
            <div className="mt-3">{bars ? <Candles bars={bars} height={440} lines={lines} overlays={overlays} volume={layers.volume} rsi={layers.rsi} /> : <div className="flex h-[440px] items-center justify-center text-[12px] tracking-normal text-ws-fg-4">{raw == null ? 'Loading the candles…' : 'No candles for this stretch.'}</div>}</div>
          </section>

          {ta && (
            <section>
              <h2 className={label}>
                What the indicators say <span className="text-ws-fg-4">· daily, as of {ta.asOf}</span>
              </h2>
              <div className="mt-3 grid grid-cols-1 divide-y divide-ws-line border-y border-ws-line sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                {(
                  [
                    ['Summary', ta.overall, null],
                    ['Oscillators', ta.oscillators.summary, ta.oscillators],
                    ['Moving averages', ta.movingAverages.summary, ta.movingAverages],
                  ] as const
                ).map(([name, call, bucket]) => (
                  <div key={name} className="py-3 sm:px-5 sm:first:pl-0">
                    <div className="text-[12px] tracking-normal text-ws-fg-3">{name}</div>
                    <div className={cx('mt-1 text-[16px] font-medium leading-6', callTone(call))}>{CALL[call] ?? call}</div>
                    {bucket && (
                      <div className="mt-1 text-[12px] tracking-normal text-ws-fg-4">
                        {bucket.buy} buy · {bucket.neutral} neutral · {bucket.sell} sell
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {ta.commentary.length > 0 && <p className="m-0 mt-3 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">{ta.commentary.join(' · ')}</p>}
            </section>
          )}

          {co && (
            <section>
              <h2 className={label}>The company</h2>
              <dl className="m-0 mt-2 grid grid-cols-1 gap-x-10 divide-y divide-ws-hair sm:grid-cols-2 sm:divide-y-0">
                <Fact k="Sector">{co.sector ?? '—'}</Fact>
                <Fact k="Industry">{co.industry ?? '—'}</Fact>
                <Fact k="Market value">{big(co.marketCap)}</Fact>
                <Fact k="Price / earnings">{co.peRatio?.toFixed(1) ?? '—'}</Fact>
                <Fact k="Earnings per share">{co.eps != null ? usd(co.eps) : '—'}</Fact>
                <Fact k="Dividend yield">{co.dividendYield != null ? `${(co.dividendYield * (co.dividendYield < 1 ? 100 : 1)).toFixed(2)}%` : '—'}</Fact>
                <Fact k="52-week range">{co.week52Low != null && co.week52High != null ? `${usd(co.week52Low)} – ${usd(co.week52High)}` : '—'}</Fact>
                <Fact k="Beta">{co.beta?.toFixed(2) ?? '—'}</Fact>
              </dl>
              {co.description && <p className="m-0 mt-3 line-clamp-4 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">{co.description}</p>}
            </section>
          )}
        </div>

        <div className="w-full shrink-0 space-y-6 xl:w-[360px]">
          {(own || inCopies.length > 0) && (
            <section>
              <h2 className={label}>Your position</h2>
              <dl className={cx('m-0 mt-2 divide-y divide-ws-hair border-y border-ws-line', !own && !h?.parts.some((x) => x.workers.length) && 'hidden')}>
                {own && (
                  <>
                    <Fact k={inCopies.length ? 'Your own shares' : 'Shares'}>{num(own.qty)}</Fact>
                    <Fact k="Average cost">{money(own.avg)}</Fact>
                    <Fact k="Value">{money(own.value)}</Fact>
                    <Fact k="Gain">
                      <span className={toneOf(own.gain ?? 0)}>
                        {own.gain == null ? '—' : `${own.gain >= 0 ? '+' : '−'}${usd(Math.abs(own.gain))}`} {own.gain != null && own.cost ? `(${pct((own.gain / own.cost) * 100)})` : ''}
                      </span>
                    </Fact>
                  </>
                )}
                {h?.parts.flatMap((x) => x.workers).map((w) => (
                  <Fact key={w.id} k="Worker">
                    <button type="button" onClick={() => useDemo.setState({ view: 'workers', workerId: w.id })} className="text-ws-fg-2 underline decoration-ws-rule underline-offset-2 hover:text-ws-fg">
                      {w.name}
                    </button>
                  </Fact>
                ))}
              </dl>
              {/* a copy's shares of it: its own, kept apart — pressed, the ticket sells from that copy alone */}
              {inCopies.map(({ c, p }) => (
                <div key={c.id} className="mt-3">
                  <div className="flex h-6 items-center justify-between text-[12px] tracking-normal">
                    <button type="button" onClick={() => useDemo.getState().openCopy(c.id)} className="text-ws-fg-2 underline decoration-ws-rule underline-offset-2 hover:text-ws-fg">
                      Copy · {c.leader.name}
                    </button>
                    {(c.status === 'active' || c.status === 'paused') && (
                      <button type="button" onClick={() => tradeFromCopy(c.id, code)} className={quiet}>
                        Sell from it
                      </button>
                    )}
                  </div>
                  <dl className="m-0 divide-y divide-ws-hair border-y border-ws-line">
                    <Fact k="Its shares">{qtyText(Number(p.qty.toFixed(6)))}</Fact>
                    <Fact k="It paid">{money(p.avg)}</Fact>
                    <Fact k="Value">{money(p.value)}</Fact>
                    <Fact k="Gain">
                      <span className={toneOf(p.gain)}>
                        {p.gain >= 0 ? '+' : '−'}
                        {usd(Math.abs(p.gain))} ({pct(p.gainPct)})
                      </span>
                    </Fact>
                  </dl>
                </div>
              ))}
            </section>
          )}
          {wide && <Ticket all={all} scope={scope} onChanged={refresh} />}
        </div>
      </div>
      {!wide && (
        <Sheet open={sheet} side="bottom" onClose={() => useTicket.setState({ sheet: false })} className="max-h-[92dvh] overflow-y-auto p-3 pb-[max(12px,env(safe-area-inset-bottom))]">
          <div className="mx-auto max-w-[440px]">
            <Ticket all={all} scope={scope} onChanged={refresh} />
          </div>
        </Sheet>
      )}
    </Page>
  )
}

export function PortfolioView() {
  const symbol = useDemo((s) => s.symbol)
  const copyId = useDemo((s) => s.copyId)
  const brokers = useDemo((s) => s.brokers)
  const go = useDemo((s) => s.go)
  const { all, shown, scope, refresh } = usePortfolio()
  if (!brokers)
    return (
      <Empty icon={<ChartPie size={26} strokeWidth={1.6} className="text-ws-icon" />} title="This workspace has no portfolio">
        It is kept apart from your broker accounts: its agents work without one. Your portfolio is in your first workspace.
      </Empty>
    )
  if (copyId) return <CopyPage id={copyId} />
  if (!all || !shown) return <div className="p-10 text-[12px] tracking-normal text-ws-fg-4">Loading the portfolio…</div>
  if (symbol) return <Stock code={symbol} all={all} scope={scope} refresh={refresh} />
  if (!all.accounts.length && !all.holdings.length && !(all.copies?.length))
    return (
      <Empty
        icon={<ChartPie size={26} strokeWidth={1.6} className="text-ws-icon" />}
        title="No account here yet"
        action={
          <Btn variant="white" className="px-3" onClick={() => go('apps')}>
            Connect a broker
          </Btn>
        }
      >
        Connect a broker and this page shows what you hold, live, and lets you trade. Without one, agents still work — research, budgets, plans — and you can keep a record by hand through any agent.
      </Empty>
    )
  return <Overview all={all} shown={shown} scope={scope} refresh={refresh} />
}
